import React, { useMemo, useState } from 'react';
import { MapContainer, CircleMarker, Popup, TileLayer } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';

import './homeMaps.css';

const UK_CENTER = [54.6, -3.2];

function scaleRadius(count) {
  // sqrt scale keeps big counties in check
  const r = Math.sqrt(Math.max(0, count)) * 1.2;
  return Math.max(4, Math.min(22, r));
}

function colorForCount(count) {
  if (count >= 200) return '#0f172a'; // slate-900
  if (count >= 120) return '#1d4ed8'; // blue-700
  if (count >= 60) return '#0f766e'; // teal-700
  if (count >= 25) return '#7c3aed'; // violet-600
  return '#64748b'; // slate-500
}

export default function HomeCountyBubbleMap({ countiesByCountry }) {
  const navigate = useNavigate();
  const [activeCountry, setActiveCountry] = useState('All');

  const allCounties = useMemo(() => {
    const entries = [];
    for (const [, counties] of Object.entries(countiesByCountry || {})) {
      for (const c of counties || []) {
        if (!c?.centroid) continue;
        entries.push(c);
      }
    }
    return entries;
  }, [countiesByCountry]);

  const visible = useMemo(() => {
    if (activeCountry === 'All') return allCounties;
    return (countiesByCountry?.[activeCountry] || []).filter((c) => c?.centroid);
  }, [activeCountry, allCounties, countiesByCountry]);

  const maxCount = useMemo(() => {
    let m = 0;
    for (const c of visible) m = Math.max(m, c.course_count || 0);
    return m;
  }, [visible]);

  return (
    <section className="bg-white rounded-2xl shadow-xl ring-1 ring-slate-200/80 overflow-hidden h-full min-h-[520px] flex flex-col">
      <div className="p-5 sm:p-6 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">
            County course density map
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 mt-1">
            Bubble size and color reflect course count (max: {maxCount.toLocaleString()}).
          </p>
        </div>

        <select
          value={activeCountry}
          onChange={(e) => setActiveCountry(e.target.value)}
          className="h-9 text-sm border border-slate-200 rounded-lg px-2 bg-white text-slate-700"
          aria-label="Filter map by country"
        >
          <option value="All">All</option>
          <option value="England">England</option>
          <option value="Scotland">Scotland</option>
          <option value="Wales">Wales</option>
          <option value="Northern Ireland">Northern Ireland</option>
          <option value="Ireland">Ireland</option>
        </select>
      </div>

      <div className="home-leaflet-container flex-1">
        <MapContainer
          center={UK_CENTER}
          zoom={6}
          scrollWheelZoom={false}
          style={{ width: '100%', height: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {visible.map((c) => (
            <CircleMarker
              key={c.slug_county_name}
              center={[c.centroid.lat, c.centroid.lon]}
              radius={scaleRadius(c.course_count || 0)}
              pathOptions={{
                color: colorForCount(c.course_count || 0),
                weight: 2,
                fillColor: colorForCount(c.course_count || 0),
                fillOpacity: 0.55,
              }}
            >
              <Popup>
                <div className="text-sm">
                  <div className="font-semibold text-slate-900">{c.county_name}</div>
                  <div className="text-slate-600">
                    {c.country_of_county} · {Number(c.course_count || 0).toLocaleString()} courses
                  </div>
                  <button
                    type="button"
                    className="mt-2 inline-flex items-center justify-center px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs font-semibold"
                    onClick={() =>
                      navigate(`/lists/golf-courses/county-ranking/${c.slug_county_name}`)
                    }
                  >
                    View county ranking
                  </button>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>
    </section>
  );
}

